/* GEOTECH · ARC bag drawer
   - Opens instantly from the bag icon, then quietly re-checks the cart in the background.
   - Slide-in and slide-out animations (duration comes from the header section setting).
   - Raises its header section above the announcement bar while open (overlay dims + blurs the page).
   - Works with Dawn's product form / quick add (getSectionsToRender + renderContents).
   - Theme editor: re-opens after a live settings refresh so width, blur and speed changes are visible. */
(() => {
  if (window.GeoArcCartDrawerLoaded) return;
  window.GeoArcCartDrawerLoaded = true;

  const controllers = new WeakMap();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let reopenAfterEditorRefresh = false;

  const routesRoot = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const isDesignMode = () => Boolean(window.Shopify && window.Shopify.designMode);
  const cartSignature = (cart) =>
    `${cart.item_count}|${cart.total_price}|${(cart.items || []).map((item) => `${item.key}:${item.quantity}`).join(',')}`;

  class GeoArcCartDrawerController {
    constructor(host) {
      this.host = host;
      this.sectionId = host.dataset.sectionId;
      this.lastActiveElement = null;
      this.addBusy = false;
      this.lineStates = new Map();
      this.revalidateTimer = null;

      this.onHostClick = this.onHostClick.bind(this);
      this.onHostChange = this.onHostChange.bind(this);
      this.onHostSubmit = this.onHostSubmit.bind(this);
      this.onHostKeyDown = this.onHostKeyDown.bind(this);
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onDocumentKeyDown = this.onDocumentKeyDown.bind(this);
      this.onPageShow = this.onPageShow.bind(this);

      host.addEventListener('click', this.onHostClick);
      host.addEventListener('change', this.onHostChange);
      host.addEventListener('submit', this.onHostSubmit);
      host.addEventListener('keydown', this.onHostKeyDown);
      document.addEventListener('click', this.onDocumentClick);
      document.addEventListener('keydown', this.onDocumentKeyDown);
      window.addEventListener('pageshow', this.onPageShow);

      host.open = this.open.bind(this);
      host.close = this.close.bind(this);
      host.renderContents = this.renderContents.bind(this);
      host.getSectionsToRender = this.getSectionsToRender.bind(this);
      host.setActiveElement = this.setActiveElement.bind(this);
      host.applySectionHTML = this.applySectionHTML.bind(this);

      if (!host.classList.contains('is-open')) host.setAttribute('inert', '');

      if (reopenAfterEditorRefresh) {
        reopenAfterEditorRefresh = false;
        this.open({ animate: false, focus: false });
      }
    }

    destroy() {
      this.host.removeEventListener('click', this.onHostClick);
      this.host.removeEventListener('change', this.onHostChange);
      this.host.removeEventListener('submit', this.onHostSubmit);
      this.host.removeEventListener('keydown', this.onHostKeyDown);
      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('keydown', this.onDocumentKeyDown);
      window.removeEventListener('pageshow', this.onPageShow);
      clearTimeout(this.closeTimer);
      clearTimeout(this.enterTimer);
      clearTimeout(this.revalidateTimer);
      clearTimeout(this.checkoutTimer);
      this.lineStates.forEach((state) => clearTimeout(state.timer));
      this.lineStates.clear();
      if (this.host.classList.contains('is-open')) this.setPageLocked(false);
      controllers.delete(this.host);
    }

    /* ---------- Dawn compatibility ---------- */

    getSectionsToRender() {
      return this.sectionId ? [{ id: this.sectionId }] : [];
    }

    setActiveElement(element) {
      this.lastActiveElement = element;
    }

    renderContents(parsedState) {
      const sectionHTML = parsedState && parsedState.sections && parsedState.sections[this.sectionId];
      const title = parsedState && (parsedState.product_title || parsedState.title);
      // Name the exact size/colour that went in, so shoppers can confirm it at a glance.
      const variantTitle = parsedState && parsedState.product_title ? parsedState.variant_title : '';
      const detail = variantTitle && variantTitle !== 'Default Title' ? ` — ${variantTitle}` : '';
      const message = title ? `Added · ${title}${detail}` : 'Item added to your bag.';

      if (sectionHTML) {
        this.applySectionHTML(sectionHTML);
        this.open();
        this.showStatus(message);
        return;
      }

      this.open();
      this.refresh()
        .then(() => this.showStatus(message))
        .catch(() => this.showStatus('Item added. Refresh the page if your bag does not update.', true));
    }

    /* ---------- Open / close ---------- */

    get duration() {
      if (reducedMotion.matches) return 0;
      const value = getComputedStyle(this.host).getPropertyValue('--geo-cart-drawer-duration').trim();
      const ms = value.endsWith('ms') ? parseFloat(value) : parseFloat(value) * 1000;
      return Number.isFinite(ms) ? ms : 220;
    }

    open({ animate = true, focus = true } = {}) {
      const host = this.host;
      clearTimeout(this.closeTimer);
      if (host.classList.contains('is-open')) return;

      this.setPageLocked(true);
      host.removeAttribute('inert');

      if (!animate) host.classList.add('geo-no-transition');
      host.classList.add('is-open', 'is-entering');

      if (!animate) {
        requestAnimationFrame(() => requestAnimationFrame(() => host.classList.remove('geo-no-transition')));
      }

      clearTimeout(this.enterTimer);
      this.enterTimer = setTimeout(() => host.classList.remove('is-entering'), Math.max(160, this.duration + 40));

      if (focus) {
        requestAnimationFrame(() => {
          const closeButton = host.querySelector('.geo-cart-drawer__close');
          if (closeButton) closeButton.focus({ preventScroll: true });
        });
      }
    }

    close() {
      const host = this.host;
      if (!host.classList.contains('is-open')) return;

      host.classList.remove('is-open', 'is-entering');
      host.setAttribute('inert', '');

      clearTimeout(this.closeTimer);
      this.closeTimer = setTimeout(() => this.setPageLocked(false), this.duration);

      const returnTarget =
        this.lastActiveElement && document.documentElement.contains(this.lastActiveElement)
          ? this.lastActiveElement
          : document.querySelector('#cart-icon-bubble');
      if (returnTarget) returnTarget.focus({ preventScroll: true });
    }

    setPageLocked(locked) {
      const root = document.documentElement;
      const section = locked ? this.host.closest('.shopify-section') : this.raisedSection;

      if (locked) {
        const scrollbar = window.innerWidth - root.clientWidth;
        root.style.setProperty('--geo-scrollbar-compensation', `${Math.max(0, scrollbar)}px`);
        root.classList.add('geo-cart-drawer-open');
        document.body.classList.add('geo-cart-drawer-open');
        if (section) section.classList.add('geo-layer-raised');
        this.raisedSection = section;
      } else {
        root.classList.remove('geo-cart-drawer-open');
        document.body.classList.remove('geo-cart-drawer-open');
        root.style.removeProperty('--geo-scrollbar-compensation');
        if (section) section.classList.remove('geo-layer-raised');
        this.raisedSection = null;
      }
    }

    /* ---------- Events ---------- */

    onDocumentClick(event) {
      const trigger = event.target.closest('#cart-icon-bubble, [data-geo-cart-open]');
      if (!trigger || !this.host.isConnected) return;
      if (event.defaultPrevented || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      event.preventDefault();
      this.setActiveElement(trigger);
      this.open();
      this.scheduleRevalidate();
    }

    onDocumentKeyDown(event) {
      if (!this.host.classList.contains('is-open')) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.key !== 'Tab') return;
      const focusable = this.getFocusableElements();
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (!this.host.contains(document.activeElement)) {
        event.preventDefault();
        first.focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    onPageShow(event) {
      // Coming back with the browser's back button can show an old bag: force a re-check next time.
      if (event.persisted) this.host.dataset.geoCartSignature = '';
      this.resetCheckoutState();
    }

    onHostClick(event) {
      const closeTrigger = event.target.closest('[data-geo-cart-close]');
      if (closeTrigger) {
        event.preventDefault();
        this.close();
        return;
      }

      const quantityButton = event.target.closest('[data-geo-cart-quantity]');
      if (quantityButton) {
        event.preventDefault();
        const item = quantityButton.closest('.geo-cart-item');
        const input = item && item.querySelector('[data-geo-cart-quantity-input]');
        if (!input) return;
        const adjustment = quantityButton.dataset.geoCartQuantity === 'increase' ? 1 : -1;
        const current = Number.parseInt(input.value || '0', 10);
        const step = Math.max(1, Number.parseInt(input.step || '1', 10) || 1);
        const quantity = this.normalizeQuantity(
          input,
          (Number.isFinite(current) ? current : Number.parseInt(input.min || '1', 10) || 1) + adjustment * step
        );
        input.value = String(quantity);
        this.queueLineChange(quantityButton.dataset.lineKey, quantity, item);
        return;
      }

      const removeTrigger = event.target.closest('[data-geo-cart-remove]');
      if (removeTrigger) {
        event.preventDefault();
        const item = removeTrigger.closest('.geo-cart-item');
        this.queueLineChange(removeTrigger.dataset.lineKey, 0, item, true);
        return;
      }

      const addTrigger = event.target.closest('[data-geo-cart-add]');
      if (addTrigger) {
        event.preventDefault();
        this.addVariant(addTrigger.dataset.geoCartAdd, addTrigger);
      }
    }

    onHostChange(event) {
      const input = event.target.closest('[data-geo-cart-quantity-input]');
      if (!input) return;
      const parsed = Number.parseInt(input.value || '0', 10);
      const quantity = this.normalizeQuantity(input, Number.isFinite(parsed) ? parsed : Number.parseInt(input.min || '1', 10) || 1);
      input.value = String(quantity);
      this.queueLineChange(input.dataset.lineKey, quantity, input.closest('.geo-cart-item'));
    }

    normalizeQuantity(input, value) {
      const min = Math.max(1, Number.parseInt(input.min || '1', 10) || 1);
      const step = Math.max(1, Number.parseInt(input.step || '1', 10) || 1);
      const parsedMax = Number.parseInt(input.max || '', 10);
      const max = Number.isFinite(parsedMax) ? parsedMax : Infinity;
      const clamped = Math.min(max, Math.max(min, value));
      return Math.min(max, min + Math.round((clamped - min) / step) * step);
    }

    onHostSubmit(event) {
      const form = event.target.closest('[data-geo-cart-form]');
      if (!form || !this.host.contains(form)) return;

      if (this.checkoutBusy) {
        event.preventDefault();
        return;
      }

      // GEOTECH fix (Sep 2026): if a quantity change / removal / add is still travelling, wait for it.
      // The checkout form posts every line's quantity by position, so leaving mid-update could send
      // the wrong quantities (or bring a removed item back) to checkout.
      if (this.hasPendingBagChanges()) {
        event.preventDefault();
        this.checkoutQueued = true;
        const pendingButton = form.querySelector('.geo-cart-drawer__checkout');
        if (pendingButton) {
          pendingButton.classList.add('is-loading');
          pendingButton.setAttribute('aria-busy', 'true');
        }
        this.showStatus('Updating your bag…');
        return;
      }

      this.checkoutBusy = true;
      const button = form.querySelector('.geo-cart-drawer__checkout');
      if (button) {
        button.classList.add('is-loading');
        button.setAttribute('aria-busy', 'true');
        button.setAttribute('aria-disabled', 'true');
      }
      this.showStatus('Opening secure checkout…');

      clearTimeout(this.checkoutTimer);
      this.checkoutTimer = setTimeout(() => {
        this.resetCheckoutState();
        this.showStatus('Checkout did not open. Please try again.', true);
      }, 15000);
    }

    hasPendingBagChanges() {
      return this.lineStates.size > 0 || this.addBusy;
    }

    resumeQueuedCheckout() {
      if (!this.checkoutQueued || this.hasPendingBagChanges()) return;
      this.checkoutQueued = false;
      const form = this.host.querySelector('[data-geo-cart-form]');
      const button = form && form.querySelector('.geo-cart-drawer__checkout');
      if (!form || !button) return; // the bag emptied: nothing to check out
      if (typeof form.requestSubmit === 'function') form.requestSubmit(button);
      else button.click();
    }

    cancelQueuedCheckout() {
      if (!this.checkoutQueued) return;
      this.checkoutQueued = false;
      this.resetCheckoutState();
    }

    onHostKeyDown(event) {
      // Enter in a quantity box updates that quantity; it should not jump straight to checkout.
      const input = event.target.closest && event.target.closest('[data-geo-cart-quantity-input]');
      if (!input || event.key !== 'Enter') return;
      event.preventDefault();
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    resetCheckoutState() {
      this.checkoutBusy = false;
      clearTimeout(this.checkoutTimer);
      const button = this.host.querySelector('.geo-cart-drawer__checkout');
      if (!button) return;
      button.classList.remove('is-loading');
      button.removeAttribute('aria-busy');
      button.removeAttribute('aria-disabled');
    }

    getFocusableElements() {
      return [
        ...this.host.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), [tabindex]:not([tabindex="-1"])'
        ),
      ].filter((element) => !element.hidden && element.getClientRects().length > 0);
    }

    /* ---------- Data ---------- */

    scheduleRevalidate() {
      clearTimeout(this.revalidateTimer);
      // Opening motion gets the main thread first; stale-cart reconciliation happens just after it settles.
      this.revalidateTimer = setTimeout(() => this.revalidate(), Math.max(180, this.duration + 40));
    }

    revalidate() {
      if (this.revalidating) return this.revalidating;

      this.revalidating = fetch(`${routesRoot()}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' })
        .then((response) => (response.ok ? response.json() : Promise.reject(new Error('cart.js failed'))))
        .then((cart) => {
          if (cartSignature(cart) !== this.host.dataset.geoCartSignature) return this.refresh();
          return null;
        })
        .catch(() => null)
        .finally(() => {
          this.revalidating = null;
        });

      return this.revalidating;
    }

    async refresh() {
      if (!this.sectionId) throw new Error('Missing header section ID.');
      const response = await fetch(`${window.location.pathname}?section_id=${encodeURIComponent(this.sectionId)}`, {
        headers: { Accept: 'text/html' },
        cache: 'no-store',
      });
      if (!response.ok) throw new Error(`Cart refresh failed: ${response.status}`);
      this.applySectionHTML(await response.text());
    }

    applySectionHTML(html) {
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const nextDrawer = parsed.querySelector('[data-geo-cart-drawer]');
      if (!nextDrawer) throw new Error('Cart drawer markup was not returned.');

      const scroller = this.host.querySelector('.geo-cart-drawer__scroll');
      const scrollTop = scroller ? scroller.scrollTop : 0;

      this.host.innerHTML = nextDrawer.innerHTML;
      this.host.classList.toggle('is-empty', nextDrawer.classList.contains('is-empty'));
      this.host.dataset.geoCartSignature = nextDrawer.dataset.geoCartSignature || '';
      const nextStyle = nextDrawer.getAttribute('style');
      if (nextStyle) this.host.setAttribute('style', nextStyle);

      const nextScroller = this.host.querySelector('.geo-cart-drawer__scroll');
      if (nextScroller) nextScroller.scrollTop = scrollTop;

      const currentBubble = document.querySelector('#cart-icon-bubble');
      const nextBubble = parsed.querySelector('#cart-icon-bubble');
      if (currentBubble && nextBubble) {
        const hadFocus = document.activeElement === currentBubble;
        currentBubble.replaceWith(nextBubble);
        if (this.lastActiveElement === currentBubble) this.lastActiveElement = nextBubble;
        if (hadFocus) nextBubble.focus({ preventScroll: true });
      }
    }

    sectionPayload(extra) {
      return JSON.stringify({ ...extra, sections: [this.sectionId], sections_url: window.location.pathname });
    }

    queueLineChange(key, quantity, item, immediate = false) {
      if (!key) return;
      let state = this.lineStates.get(key);
      if (!state) {
        const adjacent = item?.nextElementSibling || item?.previousElementSibling;
        state = {
          desired: quantity,
          sent: null,
          inFlight: false,
          timer: null,
          item: item || null,
          adjacentKey: adjacent?.dataset?.lineKey || '',
          restoreFocus: Boolean(item?.contains(document.activeElement)),
        };
        this.lineStates.set(key, state);
      }
      state.desired = quantity;
      if (item) {
        state.item = item;
        state.restoreFocus = state.restoreFocus || item.contains(document.activeElement);
      }
      if (state.item) {
        state.item.dataset.geoLineState = quantity === 0 ? 'removing' : 'updating';
        state.item.setAttribute('aria-busy', 'true');
      }
      this.showStatus(quantity === 0 ? 'Removing item…' : 'Updating quantity…');
      clearTimeout(state.timer);
      if (state.inFlight) return;
      if (immediate) this.flushLineChange(key);
      else state.timer = setTimeout(() => this.flushLineChange(key), 90);
    }

    async flushLineChange(key) {
      const state = this.lineStates.get(key);
      if (!state || state.inFlight) return;
      clearTimeout(state.timer);
      state.timer = null;
      state.inFlight = true;
      const sentQuantity = state.desired;
      state.sent = sentQuantity;

      try {
        const response = await fetch(`${routesRoot()}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: this.sectionPayload({ id: key, quantity: sentQuantity }),
        });
        const data = await response.json();
        if (!response.ok || data.status) throw new Error(data.description || data.message || 'Unable to update this item.');

        // If the shopper tapped again while this request was travelling, do not paint stale intermediate HTML.
        if (state.desired !== sentQuantity) {
          state.inFlight = false;
          this.flushLineChange(key);
          return;
        }

        const html = data.sections && data.sections[this.sectionId];
        if (html) this.applySectionHTML(html);
        else await this.refresh();

        this.publishCartUpdate(data);
        this.showStatus(sentQuantity === 0 ? 'Item removed from your bag.' : 'Your bag is updated.');
        this.restoreLineFocus(state, sentQuantity);
        this.lineStates.delete(key);
      } catch (error) {
        this.lineStates.delete(key);
        this.cancelQueuedCheckout(); // let the shopper see the message before checking out
        try {
          await this.refresh();
        } catch (_) {
          // Keep the existing drawer usable when recovery cannot be fetched.
        }
        this.showStatus(error.message || 'Unable to update your bag.', true);
        this.restoreLineFocus(state, 1);
      } finally {
        const current = this.lineStates.get(key);
        // A newer request may already be using the same state object. Never clear its in-flight flag.
        if (current && current.sent === sentQuantity) current.inFlight = false;
        if ((!current || current.sent === sentQuantity) && state.item && state.item.isConnected) {
          state.item.removeAttribute('data-geo-line-state');
          state.item.removeAttribute('aria-busy');
        }
        this.resumeQueuedCheckout();
      }
    }

    async addVariant(variantId, button) {
      if (!variantId || this.addBusy) return;
      this.addBusy = true;
      this.setControlBusy(button, true);
      this.showStatus('Adding item…');

      try {
        const response = await fetch(`${routesRoot()}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: this.sectionPayload({ items: [{ id: Number(variantId), quantity: 1 }] }),
        });
        const data = await response.json();
        if (!response.ok || data.status) throw new Error(data.description || data.message || 'Unable to add this item.');

        const html = data.sections && data.sections[this.sectionId];
        if (html) this.applySectionHTML(html);
        else await this.refresh();

        this.publishCartUpdate(data);
        const card = button && button.closest('.geo-cart-upsell__card');
        const title =
          (data.items && data.items[0] && data.items[0].product_title) ||
          (card && card.querySelector('a') && card.querySelector('a').textContent.trim());
        this.showStatus(title ? `Added · ${title}` : 'Item added to your bag.');
      } catch (error) {
        this.cancelQueuedCheckout();
        this.showStatus(error.message || 'Unable to add this item.', true);
      } finally {
        this.addBusy = false;
        this.setControlBusy(button, false);
        this.resumeQueuedCheckout();
      }
    }

    restoreLineFocus(state, quantity) {
      if (!state?.restoreFocus) return;
      requestAnimationFrame(() => {
        let target = null;
        if (quantity > 0) {
          const currentItem = [...this.host.querySelectorAll('.geo-cart-item')]
            .find((item) => item.dataset.lineKey === state.item?.dataset.lineKey);
          target = currentItem?.querySelector('[data-geo-cart-quantity-input], .geo-cart-item__title');
        } else if (state.adjacentKey) {
          const adjacentItem = [...this.host.querySelectorAll('.geo-cart-item')]
            .find((item) => item.dataset.lineKey === state.adjacentKey);
          target = adjacentItem?.querySelector('.geo-cart-item__title, [data-geo-cart-quantity-input]');
        }
        target ||= this.host.querySelector('.geo-cart-drawer__shop-button, .geo-cart-drawer__close');
        target?.focus({ preventScroll: true });
      });
    }

    publishCartUpdate(cartData) {
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'geo-cart-drawer', cartData });
      }
    }

    setControlBusy(control, isBusy) {
      if (!control) return;
      control.toggleAttribute('aria-busy', isBusy);
      if (isBusy) control.setAttribute('aria-disabled', 'true');
      else control.removeAttribute('aria-disabled');
      if ('disabled' in control) control.disabled = isBusy;
    }

    showStatus(message, isError = false) {
      const status = this.host.querySelector('[data-geo-cart-status]');
      const text = this.host.querySelector('[data-geo-cart-status-text]');
      if (!status || !text) return;
      text.textContent = message;
      status.hidden = false;
      status.toggleAttribute('data-error', isError);
    }
  }

  const enhance = (host) => {
    if (!host || controllers.has(host)) return;
    controllers.set(host, new GeoArcCartDrawerController(host));
  };

  if (!customElements.get('cart-drawer')) {
    customElements.define(
      'cart-drawer',
      class extends HTMLElement {
        connectedCallback() {
          if (this.hasAttribute('data-geo-cart-drawer')) enhance(this);
        }

        disconnectedCallback() {
          const controller = controllers.get(this);
          if (controller) controller.destroy();
        }
      }
    );
  }

  const initialize = () => document.querySelectorAll('cart-drawer[data-geo-cart-drawer]').forEach(enhance);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();

  // Theme editor: remember an open bag across a live section refresh.
  document.addEventListener('shopify:section:unload', (event) => {
    const host = event.target && event.target.querySelector && event.target.querySelector('cart-drawer[data-geo-cart-drawer]');
    if (host && host.classList.contains('is-open') && isDesignMode()) reopenAfterEditorRefresh = true;
  });
  document.addEventListener('shopify:section:load', initialize);
})();
